import React, { useCallback, useEffect, useRef, useState } from "react";
import { Soundfont } from "smplr";
import { useIsSmallScreen } from "../../App";
import { useSettings } from "../../utils/settings";
import { EBChant, Neume } from "./EBChant";
import { getNeumesFromGabc } from "./getNeumesFromGabc";
import { NOTES, getTransposeByFromMode, transposeNote } from "./note-utils";

const useOptionalInterval = (
    callback: () => void,
    period: number,
    enabled: boolean
) => {
    const enabledCallback = enabled ? callback : undefined;

    const intervalRef = useRef<number | undefined>(undefined);
    const savedCallback = useRef<(() => void) | undefined>(enabledCallback);

    useEffect(() => {
        savedCallback.current = enabledCallback;
    }, [enabledCallback]);

    useEffect(() => {
        if (enabled) {
            const tick = () => {
                if (savedCallback.current) {
                    savedCallback.current();
                }
            };

            const intervalId: any = setInterval(tick, period);
            intervalRef.current = intervalId;

            return () => {
                clearInterval(intervalRef.current);
            };
        }
    }, [period, enabled]);

    return intervalRef;
};

export const getNextNeumeIndex = (
    neumes: Neume[],
    index: [number, number]
): [number, number] | undefined => {
    try {
        const neume = neumes[index[0]];

        let notesInNeume = 0;
        neume.elements.forEach((element) => {
            notesInNeume += element.notes.length;
        });

        if (index[1] === notesInNeume - 1) {
            let newNeume = index[0] + 1;

            while (
                newNeume < neumes.length &&
                neumes[newNeume].elements[0].notes.length === 0
            ) {
                newNeume += 1;
            }

            if (newNeume === neumes.length) {
                return undefined;
            } else {
                return [newNeume, 0];
            }
        } else {
            return [index[0], index[1] + 1];
        }
    } catch {
        return undefined;
    }
};

export const getPauseAfterNeume = (
    neumes: Neume[],
    index: [number, number],
    baseDuration: number
): number => {
    let pause = 0;

    const nextIndex = getNextNeumeIndex(neumes, index);

    if (!nextIndex || nextIndex[1] !== 0) {
        return 0;
    }

    let newNeume = index[0] + 1;

    while (
        newNeume < neumes.length &&
        neumes[newNeume].elements[0].notes.length === 0
    ) {
        console.log(neumes[newNeume]);
        if (neumes[newNeume].elements[0].type === "half bar") {
            pause += baseDuration;
        }
        if (neumes[newNeume].elements[0].type === "full bar") {
            pause += baseDuration * 2;
        }

        newNeume += 1;
    }

    return pause;
};

interface SoundfontNote {
    label: string;
    duration: number;
}

export const getSoundfontNote = (
    neumes: Neume[],
    index: [number, number],
    baseDuration: number,
    tone: string,
    transposeBy: number
): SoundfontNote | undefined => {
    const neume = neumes[index[0]];

    let noteCount = 0;

    let soundfontNote: SoundfontNote | undefined = undefined;

    for (const element of neume.elements) {
        for (const note of element.notes) {
            if (noteCount === index[1]) {
                const pitch = note.pitch;

                let label = NOTES[pitch];
                let duration = baseDuration;

                if (note.isNoteFlat) {
                    label = label.slice(0, 1) + "b" + label.slice(1, 2);
                }

                label = transposeNote(
                    label,
                    getTransposeByFromMode(tone.slice(0, 1)) + transposeBy
                );

                if (note.hasMora) {
                    duration *= 2;
                }

                soundfontNote = { label, duration };
            } else {
                if (
                    noteCount === index[1] + 1 &&
                    note.isQuilisma &&
                    soundfontNote
                ) {
                    soundfontNote.duration *= 1.25;
                }
            }

            noteCount += 1;
        }
    }

    return soundfontNote;
};

const NOTE_LENGTH = 500;

type PlayerState =
    | { playing: false }
    | {
          playing: true;
          highlightedNote: [number, number];
          timeUntilNextNote: number;
      };

export const ChantExplorer: React.FC<{ gabc: string; tone: string }> = (
    props
) => {
    const { gabc, tone } = props;

    const { neumes } = getNeumesFromGabc(gabc, false);

    const audioContextRef = useRef(new AudioContext());

    const getCurrentTime = () => {
        return audioContextRef.current.currentTime;
    };

    const playNoteRef = useRef<((note: SoundfontNote) => void) | undefined>(
        undefined
    );

    const [instrumentState, setInstrumentState] = useState<
        "initial" | "loading" | "ready"
    >("initial");

    const { settings, setSetting } = useSettings();
    const transposeByKey = "1";
    const transposeBy =
        settings.transpositions["1"] + getTransposeByFromMode(transposeByKey);

    const loadAndPlay = useCallback(
        (playerState: PlayerState) => {
            if (instrumentState === "initial") {
                setInstrumentState("loading");

                // create empty buffer
                var buffer = audioContextRef.current.createBuffer(1, 1, 22050);
                var source = audioContextRef.current.createBufferSource();
                source.buffer = buffer;
                // connect to output (your speakers)
                source.connect(audioContextRef.current.destination);
                // play the file
                source.start(0);

                Soundfont.instrument(audioContextRef.current, "flute").then(
                    () => {
                        playNoteRef.current = (note: SoundfontNote) => {
                            Soundfont.instrument(
                                audioContextRef.current,
                                "flute"
                            ).then((instrument) => {
                                instrument.play(note.label, getCurrentTime(), {
                                    duration: (note.duration + 150) / 1000,
                                });
                            });
                        };

                        setInstrumentState("ready");
                        setPlayerState(playerState);
                    }
                );
            } else {
                setPlayerState(playerState);
            }
        },
        [instrumentState]
    );

    const [playerState, setPlayerState] = useState<PlayerState>({
        playing: false,
    });

    const playerStateRef = useRef<PlayerState>(playerState);

    useEffect(() => {
        playerStateRef.current = playerState;
    }, [playerState]);

    const tickLength = NOTE_LENGTH / 4;

    useOptionalInterval(
        useCallback(() => {
            if (playerStateRef.current.playing) {
                if (playerStateRef.current.timeUntilNextNote <= 0) {
                    const nextHighlightedNote = getNextNeumeIndex(
                        neumes,
                        playerStateRef.current.highlightedNote
                    );

                    if (nextHighlightedNote) {
                        const soundfontNote = getSoundfontNote(
                            neumes,
                            nextHighlightedNote,
                            NOTE_LENGTH,
                            tone,
                            transposeBy
                        );

                        console.log(soundfontNote);

                        if (playNoteRef.current && soundfontNote) {
                            playNoteRef.current(soundfontNote);

                            const pause = getPauseAfterNeume(
                                neumes,
                                nextHighlightedNote,
                                NOTE_LENGTH
                            );

                            playerStateRef.current.highlightedNote =
                                nextHighlightedNote;
                            playerStateRef.current.timeUntilNextNote =
                                soundfontNote.duration + pause;
                        }
                    } else {
                        setPlayerState({ playing: false });
                        return;
                    }
                }

                playerStateRef.current.timeUntilNextNote -= tickLength;
                setPlayerState({ ...playerStateRef.current });
            }
        }, []),
        tickLength,
        playerState.playing && instrumentState === "ready"
    );

    const click = useIsSmallScreen() ? "Tap" : "Click";

    return (
        <div style={{ border: `1px solid black` }}>
            <div>
                {playerState.playing
                    ? `${click} anywhere to pause`
                    : `${click} a neume to play from there`}
            </div>
            <div
                onClick={() => {
                    if (playerState.playing) {
                        setPlayerState({ playing: false });
                    }
                }}
                style={{ cursor: "pointer" }}
            >
                <EBChant
                    gabc={gabc}
                    dropCap={false}
                    tone={tone}
                    onClickNeume={(neumeIndex) => {
                        if (!playerState.playing) {
                            loadAndPlay({
                                playing: true,
                                highlightedNote: [neumeIndex, -1],
                                timeUntilNextNote: 0,
                            });
                        }
                    }}
                    highlightedNote={
                        playerState.playing
                            ? playerState.highlightedNote
                            : undefined
                    }
                />
            </div>
        </div>
    );
};
